import { graphql } from '@/school-gql'

import { EnterpriseNav } from '@/components/enterprise-nav'
import { SchoolAdminPageDetailBanner } from '@/components/school-admin-page-detail-banner'
import { SchoolAdminPageHeroBanner } from '@/components/school-admin-page-hero-banner'
import { WebFooter } from '@/components/web-footer'
import { SchoolWebTopHeader } from '@/components/web-top-header/SchoolWebTopHeader'
import { useClientOnly } from '@/hooks/useClientOnly'
import { useQuery } from '@/hooks/useQuery'
import { useRouter } from 'next/router'
import { SchoolLayout } from '../../school'
import { SchoolAdminForcePasswordChangeGuard } from './SchoolAdminForcePasswordChangeGuard'

interface Props {
  children: React.ReactNode
}

export const EnterpriseLayout = ({ children }: Props): JSX.Element => {
  return (
    <SchoolLayout>
      <SchoolAdminForcePasswordChangeGuard>
        <EnterpriseLayoutInner>{children}</EnterpriseLayoutInner>
      </SchoolAdminForcePasswordChangeGuard>
    </SchoolLayout>
  )
}

export const EnterpriseNoAsideLayout = ({ children }: Props): JSX.Element => {
  return (
    <SchoolLayout>
      <SchoolAdminForcePasswordChangeGuard>
        <EnterpriseNoAsideLayoutInner>{children}</EnterpriseNoAsideLayoutInner>
      </SchoolAdminForcePasswordChangeGuard>
    </SchoolLayout>
  )
}

export const EnterpriseLayoutInner = ({ children }: Props): JSX.Element => {
  useClientOnly()

  const [{ data }] = useQuery({
    query: EnterpriseLayout_CurrentUserQuery,
  })
  const schoolName = data?.viewer?.school?.name

  return (
    <div className="grid-areas-enterprise-layout">
      <div className="grid-area-header">
        <SchoolWebTopHeader />
        <SchoolAdminPageHeroBanner
          title={schoolName ?? ''}
          showBackButton={false}
        />
      </div>
      <aside className="grid-area-nav bg-[#F5F7FC]">
        <EnterpriseNav />
      </aside>
      <article className="grid-area-content bg-white px-[72px] py-[42px]">
        {children}
      </article>
      <WebFooter className="grid-area-footer border-t border-disable" />
    </div>
  )
}

export const EnterpriseNoAsideLayoutInner = ({
  children,
}: Props): JSX.Element => {
  useClientOnly()
  const router = useRouter()

  const [{ data }] = useQuery({
    query: EnterpriseLayout_CurrentUserQuery,
  })
  const schoolName = data?.viewer?.school?.name
  const isForcePasswordChange = router.query.forcePasswordChange === '1'

  return (
    <div className="grid-areas-enterprise-no-aside-layout">
      <div className="grid-area-header">
        <SchoolWebTopHeader />
        <SchoolAdminPageDetailBanner
          title={schoolName ?? ''}
          showBackButton={!isForcePasswordChange}
        />
      </div>
      <article className="grid-area-content bg-white px-[72px] py-[42px]">
        {children}
      </article>
      <WebFooter className="grid-area-footer border-t border-disable" />
    </div>
  )
}

export function getEnterpriseLayout(page: React.ReactElement) {
  return <EnterpriseLayout>{page}</EnterpriseLayout>
}

export function getEnterpriseNoAsideLayout(page: React.ReactElement) {
  return <EnterpriseNoAsideLayout>{page}</EnterpriseNoAsideLayout>
}

const EnterpriseLayout_CurrentUserQuery = graphql(`
  query EnterpriseLayout_CurrentUserQuery {
    viewer {
      id
      school {
        id
        name
      }
    }
  }
`)
